import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import PowerIcon from "@mui/icons-material/Power"
import { Stack, Table, TableCell, TableHead, TableRow } from "@mui/material"
import Grid from "@mui/material/Unstable_Grid2"
import { useConfirm } from "material-ui-confirm"
import { ReactElement, useState } from "react"
import { useNavigate } from "react-router-dom"

import { DeviceSupersededDisplay } from "./deviceSupersededDisplay"
import HeaderBanner from "./headerBanner"
import {
    SUPPORTING_DOCS_HEADER,
    SUPPORTING_DOCS_SUBHEADER,
} from "./singleGenDeviceDisplay"
import { AlertDisplayComponent } from "../../components/alert/alertDisplay"
import AppButton from "../../components/buttons/appButton"
import DownloadDeviceAttachmentButton from "../../components/buttons/downloadDeviceAttachmentButton"
import { CommentsCardComponent } from "../../components/comments/commentsCard"
import NestedVerticalKeyValueList from "../../components/dataDisplay/nestedVerticalKeyValueList"
import DeviceAttachmentLoadingRow from "../../components/loading/skeletons/deviceAttachmentLoadingRow"
import SingleDeviceSkeleton from "../../components/loading/skeletons/singleDeviceSkeleton"
import StatusAlert from "../../components/pills/status/statusAlert"
import AssessDeviceForm from "../../components/popUps/assessDevice/assessDeviceForm"
import AlertResponseForm from "../../components/popUps/raiseAlert/raiseAlertForm"
import {
    AppCard,
    AppCardContent,
    AppCardHeader,
} from "../../components/surfaces/appCard"
import AppTableContentRow from "../../components/tables/appTableContentRow"
import { Datasets, LCTOnlyDatasets } from "../../constants/enums"
import { DRAFT_AMENDMENT, DRAFT_DEVICE } from "../../constants/generalConstants"
import useGetHeaderActions from "../../hooks/useGetHeaderActions"
import { useGetLctTtrRbac } from "../../hooks/useGetLctTtrRbac"
import {
    ASSESS_DEMAND_DEVICE_NAV_LINK,
    SINGLE_DEMAND_DEVICE_DRAFT_NAV_LINK,
    SINGLE_GEN_DEVICE_NAV_LINK,
} from "../../router/routes"
import useDeleteAlert from "../../services/middleware/hooks/deviceRegisters/useDeleteAlert"
import useDeleteDevice from "../../services/middleware/hooks/deviceRegisters/useDeleteDevice"
import useUploadedDeviceAttachments from "../../services/middleware/hooks/devices/common/useUploadedDeviceAttachments"
import { ISingleDevice } from "../../services/middleware/mappers/singleDeviceMapper"
import { GRID_SPACING } from "../../themes/defaultThemeParams"
import {
    getAmendDeviceUrl,
    getContinueDraftAmendmentUrl,
    getContinueDraftversionUrl,
    getNewVersionUrl,
} from "../../utils/routes/routeUtils"

interface ISingleDemandDeviceDisplayProps {
    data: ISingleDevice
    mutate: () => void
    isLoading: boolean
    dataset: Datasets
    isValidating: boolean
}

/**
 * Returns webpage for single demand device summary page.
 * @example
 * < SingleDemandDevice data={data} mutate={mutate} dataset={dataset}/>
 */
const LINKED_INVERTER_HEADER = "Linked Inverter Device"
const LINKED_INVERTER_SUBHEADER = (linkedInverterRef: string): string =>
    `This EVCP is part of a V2G bundle. The linked inverter ${linkedInverterRef} ` +
    "will be automatically selected during the connection application."

const SingleDemandDeviceDisplay = ({
    data,
    mutate,
    isLoading,
    dataset,
    isValidating,
}: ISingleDemandDeviceDisplayProps): ReactElement => {
    const [isAlertFormOpen, setIsAlertFormOpen] = useState(false)
    const [isAssessmentFormOpen, setIsAssessmentFormOpen] = useState(false)
    const confirm = useConfirm()
    const navigate = useNavigate()
    const { deleteResponse } = useDeleteAlert()
    const { deleteDemandDevice, isDeleting } = useDeleteDevice()
    const {
        alert,
        status,
        deviceRef,
        draftAmendmentRef,
        draftVersionRef,
        supersededDeviceRef,
        isSubscribed,
    } = data.header
    const { linkedInverterRef } = data.header
    const { canReadAlerts, canDeleteAlert, canReadComments } = useGetLctTtrRbac(
        deviceRef,
        dataset,
        !!linkedInverterRef
    )

    const isDraftDevice = status === DRAFT_DEVICE
    const isDraftAmendment = status === DRAFT_AMENDMENT

    const {
        uploadedAttachments,
        isLoading: isLoadingUploadedAttachments,
        isValidating: isValidatingUploadedAttachments,
        error: uploadedAttachmentsError,
    } = useUploadedDeviceAttachments(
        dataset,
        deviceRef,
        data.header.attachmentUuid,
        isDraftAmendment
    )

    const uploadedAttachmentsAreValid =
        !isLoadingUploadedAttachments &&
        !isValidatingUploadedAttachments &&
        !uploadedAttachmentsError &&
        !!uploadedAttachments

    const isNotADraft = !isDraftAmendment && !isDraftDevice

    const deleteAlert = (): void => {
        confirm({
            description:
                "This will permanently remove the alert on device " +
                `${deviceRef}`,
        })
            .then(async () => {
                deleteResponse(deviceRef, dataset).then(mutate)
            })
            .catch(() => {
                // ...
            })
    }

    /** EVAC assessment form is a simple status pop up. The others are
     * more complex and require a new page
     */
    const onClickAssessDevice = (): void => {
        if (dataset === Datasets.EVAC) {
            setIsAssessmentFormOpen(true)
        } else {
            navigate(
                ASSESS_DEMAND_DEVICE_NAV_LINK(
                    dataset as unknown as LCTOnlyDatasets,
                    deviceRef
                )
            )
        }
    }

    const onClickRaiseAlert = (): void => setIsAlertFormOpen(true)

    const onClickAmendDevice = (): void =>
        navigate(getAmendDeviceUrl(dataset, deviceRef))

    const onClickContinueDraftAmendment = (): void =>
        navigate(getContinueDraftAmendmentUrl(dataset, deviceRef))

    const onClickContinueDraftversion = (): void =>
        navigate(getContinueDraftversionUrl(dataset, deviceRef))

    const onClickNewVersion = (): void =>
        navigate(getNewVersionUrl(dataset, deviceRef))

    const onClickViewDraftAmendment = (): void =>
        navigate(
            SINGLE_DEMAND_DEVICE_DRAFT_NAV_LINK(
                LCTOnlyDatasets[dataset as keyof typeof LCTOnlyDatasets],
                draftAmendmentRef ?? ""
            )
        )

    const onClickViewDraftVersion = (): void =>
        navigate(
            SINGLE_DEMAND_DEVICE_DRAFT_NAV_LINK(
                LCTOnlyDatasets[dataset as keyof typeof LCTOnlyDatasets],
                draftVersionRef ?? ""
            )
        )

    const onClickDeleteDevice = (): Promise<void> =>
        confirm({
            description: `This will permanently remove the device ${deviceRef}`,
        })
            .then(() => {
                deleteDemandDevice(deviceRef, dataset)
            })
            .catch(() => {
                // ...
            })

    const headerActions = useGetHeaderActions(data, dataset, {
        onClickRaiseAlert,
        onClickAssessDevice,
        onClickAmendDevice,
        onClickContinueDraftAmendment,
        onClickContinueDraftversion,
        onClickNewVersion,
        onClickViewDraftAmendment,
        onClickViewDraftVersion,
        onClickDeleteDevice,
    })

    let subSubtitle
    if (dataset === Datasets.HP) {
        subSubtitle = "Heat Pump Device"
    } else if (dataset === Datasets.EB) {
        subSubtitle = "Electric Boiler Device"
    } else if (dataset === Datasets.EVAC) {
        subSubtitle = "Electric Vehicle (Alternating Current)"
    } else if (dataset === Datasets.EVDC) {
        subSubtitle = "Electric Vehicle (Direct Current)"
    } else if (dataset === Datasets.EVAC_DRAFT) {
        subSubtitle = "Electric Vehicle (Alternating Current) Draft"
    } else if (dataset === Datasets.EVDC_DRAFT) {
        subSubtitle = "Electric Vehicle (Direct Current) Draft"
    } else if (dataset === Datasets.HP_DRAFT) {
        subSubtitle = "Heat Pump Device Draft"
    } else if (dataset === Datasets.EB_DRAFT) {
        subSubtitle = "Electric Boiler Device Draft"
    } else if (dataset === Datasets.ACU) {
        subSubtitle = "Air Conditioning Unit"
    } else if (dataset === Datasets.ACU_DRAFT) {
        subSubtitle = "Air Conditioning Unit Draft"
    } else {
        subSubtitle = "Technology Type Not Recognised"
    }

    if (isDeleting) {
        return <SingleDeviceSkeleton />
    }

    return (
        <>
            <Grid container spacing={GRID_SPACING}>
                <Grid xs={12}>
                    <Stack spacing={GRID_SPACING}>
                        <HeaderBanner
                            title={data.header.deviceRef}
                            subtitle={data.header.manufacturer}
                            subSubtitle={subSubtitle}
                            actions={headerActions}
                            Icon={<PowerIcon />}
                            isValidating={isValidating}
                        />
                        {alert && canReadAlerts && (
                            <AlertDisplayComponent
                                canDeleteAlerts={canDeleteAlert(
                                    alert?.alerterUsername
                                )}
                                alert={alert}
                                deleteAlert={deleteAlert}
                            />
                        )}
                        {supersededDeviceRef && (
                            <DeviceSupersededDisplay
                                supersededDeviceRef={supersededDeviceRef}
                                latestDeviceRef={deviceRef}
                            />
                        )}
                    </Stack>
                </Grid>
                <Grid md={6} xs={12}>
                    <Stack spacing={GRID_SPACING}>
                        <AppCard>
                            <AppCardHeader
                                title="Device Status"
                                titleTypographyProps={{
                                    variant: "h5",
                                    fontWeight: "500",
                                }}
                                subheader="This is the current status of the device. It is assigned by ENA appointed evaluators"
                            />
                            <AppCardContent>
                                <Stack spacing={GRID_SPACING}>
                                    <StatusAlert
                                        status={data?.header?.status}
                                    />
                                </Stack>
                            </AppCardContent>
                        </AppCard>
                        <AppCard>
                            <NestedVerticalKeyValueList
                                nestedKwargs={data?.content}
                            />
                        </AppCard>
                    </Stack>
                </Grid>
                <Grid md={6} xs={12}>
                    <Stack spacing={GRID_SPACING}>
                        {linkedInverterRef && (
                            <AppCard>
                                <AppCardHeader
                                    title={LINKED_INVERTER_HEADER}
                                    subheader={LINKED_INVERTER_SUBHEADER(
                                        linkedInverterRef
                                    )}
                                    titleTypographyProps={{
                                        variant: "h5",
                                        fontWeight: "500",
                                    }}
                                />
                                <AppCardContent>
                                    <AppButton
                                        fullWidth
                                        variant="outlined"
                                        color="primary"
                                        endIcon={<OpenInNewIcon />}
                                        onClick={() =>
                                            window.open(
                                                SINGLE_GEN_DEVICE_NAV_LINK(
                                                    linkedInverterRef
                                                ),
                                                "_blank",
                                                "noopener,noreferrer"
                                            )
                                        }
                                    >
                                        View linked inverter
                                    </AppButton>
                                </AppCardContent>
                            </AppCard>
                        )}
                        <AppCard>
                            <AppCardHeader
                                title={SUPPORTING_DOCS_HEADER}
                                subheader={SUPPORTING_DOCS_SUBHEADER}
                                titleTypographyProps={{
                                    variant: "h5",
                                    fontWeight: "500",
                                }}
                            />
                            <AppCardContent>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow hover>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Description</TableCell>
                                            <TableCell align="center">
                                                Restricted
                                            </TableCell>
                                            <TableCell align="center" />
                                        </TableRow>
                                        {isLoadingUploadedAttachments && (
                                            <>
                                                <DeviceAttachmentLoadingRow />
                                                <DeviceAttachmentLoadingRow />
                                                <DeviceAttachmentLoadingRow />
                                            </>
                                        )}

                                        {uploadedAttachmentsAreValid && (
                                            <>
                                                {uploadedAttachments.length ===
                                                    0 && (
                                                    <AppTableContentRow>
                                                        <TableCell colSpan={4}>
                                                            No supporting
                                                            documents have been
                                                            uploaded for this
                                                            device
                                                        </TableCell>
                                                    </AppTableContentRow>
                                                )}
                                                {uploadedAttachments.map(
                                                    (summary) => (
                                                        <AppTableContentRow
                                                            key={
                                                                summary.fileName
                                                            }
                                                            hover
                                                        >
                                                            <TableCell>
                                                                {
                                                                    summary.fileName
                                                                }
                                                            </TableCell>
                                                            <TableCell>
                                                                {
                                                                    summary.description
                                                                }
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {summary.private && (
                                                                    <CheckCircleIcon color="success" />
                                                                )}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <DownloadDeviceAttachmentButton
                                                                    deviceSysRef={
                                                                        data
                                                                            .header
                                                                            .deviceRef
                                                                    }
                                                                    fileName={
                                                                        summary.fileName
                                                                    }
                                                                    dataset={
                                                                        dataset
                                                                    }
                                                                    attachmentUuid={
                                                                        data
                                                                            .header
                                                                            .attachmentUuid
                                                                    }
                                                                    isAmendment={
                                                                        isDraftAmendment
                                                                    }
                                                                />
                                                            </TableCell>
                                                        </AppTableContentRow>
                                                    )
                                                )}
                                            </>
                                        )}
                                    </TableHead>
                                </Table>
                            </AppCardContent>
                        </AppCard>
                        {canReadComments && isNotADraft && (
                            <CommentsCardComponent
                                deviceSystemRef={data.header.deviceRef}
                                comments={data.header.comments}
                                mutate={mutate}
                                pinnedCommentId={data.header.pinnedComment}
                                fetchingComments={isLoading}
                                dataset={dataset}
                                isSubscribed={isSubscribed}
                                isThreadUnread={data.header.isThreadUnread}
                                lastReadAt={data.header.lastReadAt}
                                lastReadBy={data.header.lastReadBy}
                                isV2gDevice={!!linkedInverterRef}
                            />
                        )}
                    </Stack>
                </Grid>
            </Grid>
            <AlertResponseForm
                deviceSystemRef={data.header.deviceRef}
                isOpen={isAlertFormOpen}
                setIsOpen={setIsAlertFormOpen}
                mutate={mutate}
                dataset={dataset}
            />
            <AssessDeviceForm
                deviceRef={data.header.deviceRef}
                deviceStatus={data.header.status}
                basedOnPublishedDate={data.header.publishedDate}
                isOpen={isAssessmentFormOpen}
                setIsOpen={setIsAssessmentFormOpen}
                mutate={mutate}
                mode="LCT"
                isV2gDevice={!!data.header.linkedInverterRef}
            />
        </>
    )
}

export default SingleDemandDeviceDisplay
